import { cva } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import * as React from 'react'
import { cn } from '../../lib/cn'
import type { ButtonProps } from './button.types'

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-(--space-md) whitespace-nowrap',
    'cursor-pointer border border-transparent shadow-none',
    'rounded-(--radius-button)',
    'transition-[color,background-color,border-color,box-shadow,transform]',
    'focus-visible:outline-none focus-visible:shadow-(--focus-shadow)',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/88 hover:shadow-sm active:bg-primary/80 active:shadow-(--shadow-pressed)',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80 active:shadow-(--shadow-pressed)',
        'destructive-outline':
          'bg-transparent border border-destructive text-destructive hover:bg-destructive/10 active:bg-destructive/20 active:shadow-(--shadow-pressed-soft)',
        secondary:
          'bg-button-secondary text-secondary-foreground border border-border hover:bg-button-secondary/80 hover:shadow-sm active:bg-secondary active:shadow-(--shadow-pressed-soft)',
        tertiary:
          'bg-transparent text-accent hover:bg-accent/10 active:bg-accent/20 active:shadow-(--shadow-pressed-soft)',
        ghost:
          'bg-transparent hover:bg-foreground/[8%] text-secondary-foreground active:bg-secondary active:shadow-(--shadow-pressed-soft)',
        'ghost-reveal':
          'bg-transparent border border-transparent text-secondary-foreground hover:bg-button-secondary hover:border-border active:shadow-(--shadow-pressed-soft)',
        accent:
          'bg-accent text-accent-foreground hover:bg-accent-400 hover:shadow-sm active:bg-accent-600 active:shadow-(--shadow-pressed)',
        'accent-outline':
          'bg-transparent border border-accent text-accent-700 hover:bg-accent-50 active:bg-accent-100 active:shadow-(--shadow-pressed-soft)',
        'accent-soft':
          'bg-accent-100 border border-accent text-accent-700 hover:bg-accent-200 active:bg-accent-300 active:shadow-(--shadow-pressed-soft)',
        nav: 'bg-transparent border border-dashed border-border text-secondary-foreground hover:bg-secondary/50 hover:border-(--color-primary) active:bg-secondary active:shadow-(--shadow-pressed-soft)',
      },
      size: {
        xs: 'size-4 [&_svg]:size-3 gap-(--space-xs)',
        sm: 'btn-small [&_svg]:size-3.5 gap-(--space-sm)',
        md: 'btn-medium [&_svg]:size-4 gap-(--space-sm)',
        lg: 'btn-large [&_svg]:size-5 gap-(--space-md)',
      },
      shape: {
        text: '',
        icon: 'aspect-square p-0',
      },
    },
    compoundVariants: [
      {
        shape: 'text',
        size: 'xs',
        class:
          'px-(--space-sm) has-[>svg:first-child]:pl-(--space-xs) has-[>svg:last-child]:pr-(--space-xs)',
      },
      {
        shape: 'text',
        size: 'sm',
        class:
          'px-(--space-md) has-[>svg:first-child]:pl-(--space-sm) has-[>svg:last-child]:pr-(--space-sm)',
      },
      {
        shape: 'text',
        size: 'md',
        class:
          'px-(--space-lg) has-[>svg:first-child]:pl-(--space-md) has-[>svg:last-child]:pr-(--space-md)',
      },
      {
        shape: 'text',
        size: 'lg',
        class:
          'px-(--space-xl) has-[>svg:first-child]:pl-(--space-lg) has-[>svg:last-child]:pr-(--space-lg)',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      shape: 'text',
    },
  },
)

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      asChild = false,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot.Slot : 'button'
    return (
      <Comp
        ref={ref}
        {...(asChild ? {} : { type })}
        className={cn(buttonVariants({ variant, size, shape }), className)}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button }
